import React, { useState, useEffect } from 'react';
import moment from 'moment';

export const PurgeConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    cutoffDate,
    previewData = null,
    isPurging = false,
}) => {
    const [confirmInput, setConfirmInput] = useState('');

    useEffect(() => {
        if (isOpen) {
            setConfirmInput('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const totalEligible = previewData?.totalEligible ?? 0;
    const breakdown = previewData?.breakdown || {};
    const delinquentCount = breakdown.delinquent ?? 0;
    const formattedDate = cutoffDate ? moment(cutoffDate).format('MMMM DD, YYYY') : '';
    
    // The user must type the exact count of orders to confirm (e.g. "900")
    const expectedConfirmation = String(totalEligible);
    const isInputMatched = confirmInput.trim() === expectedConfirmation && totalEligible > 0;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && !isPurging) {
            onClose();
        }
    };

    return (
        <div
            className="purge-modal-overlay"
            onClick={handleBackdropClick}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 99999,
                padding: '20px',
                animation: 'purgeFadeIn 0.2s ease-out'
            }}
        >
            <div
                className="purge-modal-content"
                style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '16px',
                    maxWidth: '480px',
                    width: '100%',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.18)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: 'inherit'
                }}
            >
                {/* Header */}
                <div
                    style={{
                        padding: '22px 28px',
                        background: '#fff7f7',
                        borderBottom: '1px solid #fee2e2',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}
                >
                    <div
                        style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            backgroundColor: '#b91c1c',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            flexShrink: 0
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                            <path d="M12 9v4" />
                            <path d="M12 17h.01" />
                        </svg>
                    </div>
                    <h5 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#b91c1c' }}>
                        Confirm permanent purge
                    </h5>
                </div>

                {/* Body */}
                <div style={{ padding: '24px 28px' }}>
                    <p style={{ fontSize: '15px', color: '#4b5563', margin: '0 0 20px 0', lineHeight: 1.5 }}>
                        You are about to permanently delete <strong style={{ color: '#111827', fontWeight: '700' }}>{totalEligible} orders</strong>. This is logged and cannot be reversed.
                    </p>

                    {/* Information summary card */}
                    <div
                        style={{
                            background: '#f9fafb',
                            border: '1px solid #f3f4f6',
                            borderRadius: '12px',
                            padding: '18px 20px',
                            marginBottom: '22px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' }}>
                            <span style={{ color: '#6b7280' }}>Created before</span>
                            <span style={{ color: '#111827', fontWeight: '500', fontFamily: 'monospace', fontSize: '15px' }}>
                                {formattedDate}
                            </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' }}>
                            <span style={{ color: '#6b7280' }}>Orders to purge</span>
                            <span style={{ color: '#111827', fontWeight: '500', fontFamily: 'monospace', fontSize: '15px' }}>
                                {totalEligible}
                            </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' }}>
                            <span style={{ color: '#6b7280' }}>Includes delinquent</span>
                            <span style={{ color: '#111827', fontWeight: '500', fontFamily: 'monospace', fontSize: '15px' }}>
                                {delinquentCount}
                            </span>
                        </div>
                    </div>

                    {/* Instruction text */}
                    <div style={{ fontSize: '14px', color: '#374151', marginBottom: '10px' }}>
                        Type <strong style={{ fontWeight: '700', color: '#111827' }}>{totalEligible}</strong> to confirm.
                    </div>

                    {/* Confirmation Input */}
                    <input
                        type="text"
                        disabled={isPurging}
                        value={confirmInput}
                        onChange={(e) => setConfirmInput(e.target.value)}
                        placeholder={`Type  ${totalEligible}`}
                        style={{
                            width: '100%',
                            padding: '12px 16px',
                            borderRadius: '10px',
                            border: '1.5px solid #ef4444',
                            backgroundColor: '#ffffff',
                            color: '#111827',
                            fontSize: '16px',
                            textAlign: 'center',
                            fontFamily: 'monospace',
                            outline: 'none',
                            letterSpacing: '0.1em',
                            boxSizing: 'border-box'
                        }}
                    />

                    {/* Buttons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '24px' }}>
                        <button
                            type="button"
                            disabled={isPurging}
                            onClick={onClose}
                            style={{
                                flex: 1,
                                padding: '12px 16px',
                                borderRadius: '10px',
                                border: '1.5px solid #d1d5db',
                                background: '#ffffff',
                                color: '#111827',
                                fontSize: '15px',
                                fontWeight: '700',
                                cursor: isPurging ? 'not-allowed' : 'pointer'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={!isInputMatched || isPurging}
                            onClick={onConfirm}
                            style={{
                                flex: 1,
                                padding: '12px 16px',
                                borderRadius: '10px',
                                border: 'none',
                                background: isInputMatched && !isPurging ? '#b91c1c' : '#e5e7eb',
                                color: isInputMatched && !isPurging ? '#ffffff' : '#9ca3af',
                                fontSize: '15px',
                                fontWeight: '700',
                                cursor: isInputMatched && !isPurging ? 'pointer' : 'not-allowed',
                                transition: 'all 0.15s ease'
                            }}
                        >
                            {isPurging ? 'Purging...' : 'Purge Permanently'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PurgeConfirmModal;
